"use client";

import React, { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Compass, Hammer, Terminal, Cpu, ArrowRight, Sparkles, CircleCheck as CheckCircle2, BookOpen } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import type { Post } from "@/integrations/wordpress/types";
interface LocalizedText {
  vi: string;
  en: string;
}
interface Stage {
  step: string;
  title: LocalizedText;
  tag: LocalizedText;
  age: LocalizedText;
  summary: LocalizedText;
  milestone: LocalizedText;
  skills: {
    vi: string[];
    en: string[];
  };
  image: string;
  srcset: string;
  sizes: string;
  iconName: "compass" | "hammer" | "terminal" | "cpu";
}
const content = {
  specRail: {
    vi: "Lộ trình đào tạo chuẩn mực // 4–16 tuổi",
    en: "Pedagogical Blueprint // Ages 4–16"
  },
  heading: {
    vi: "4 giai đoạn kiến tạo bản lĩnh Engineering toàn diện",
    en: "The four stages of engineering mastery"
  },
  description: {
    vi: "Mỗi chặng đường được thiết kế tương thích chuẩn xác với từng mốc phát triển tư duy của trẻ, ứng dụng các công cụ chuẩn công nghiệp như LEGO và Robotics thực thụ. Thay vì những bài học rời rạc, học viên tiếp cận một lộ trình thống nhất nơi kết cấu cơ khí, tư duy Coding và chẩn đoán phần cứng bổ trợ chặt chẽ lẫn nhau.",
    en: "Every stage introduces industry-standard tools matched to your child's developmental readiness. Rather than switching between disconnected projects, students progress through a unified track where physical assembly, computational logic, and hardware diagnostics build naturally upon each other."
  },
  bookTrial: {
    vi: "Đăng ký đánh giá năng lực miễn phí",
    en: "Book a free assessment"
  },
  viewCourses: {
    vi: "Xem tất cả khóa học",
    en: "View all courses"
  },
  goalLabel: {
    vi: "Mục tiêu:",
    en: "Goal:"
  },
  competenciesLabel: {
    vi: "Năng lực cốt lõi",
    en: "Core Competencies"
  },
  curriculumDetails: {
    vi: "Khám phá chi tiết chương trình",
    en: "Explore curriculum details"
  },
  relatedProgram: {
    vi: "Xem chương trình liên quan",
    en: "View related program"
  },
  noteTitle: {
    vi: "Đánh giá năng lực liên tục cùng chuyên gia",
    en: "Continuous mentor-guided assessments"
  },
  noteDescription: {
    vi: "Học viên không học vẹt mã lệnh hay lý thuyết suông. Mỗi cột mốc đều bao gồm phần thuyết trình mô hình thực tế và buổi thẩm định chuyên sâu tại phòng lab với tỷ lệ tiêu chuẩn 1 giáo viên : 6 học sinh.",
    en: "Students do not just memorize scripts. Every milestone includes a live build demonstration and an assessment session in our 1:6 ratio laboratories."
  },
  readGuide: {
    vi: "Xem cẩm nang phụ huynh",
    en: "Read parent guide"
  }
};
const defaultMascot = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-sizes",
  alt: "ROBOTO Academy Mascot"
};
const stages: Stage[] = [{
  step: "01",
  title: {
    vi: "Khám phá (Explore)",
    en: "Explore"
  },
  tag: {
    vi: "Giai đoạn 01 // Nền tảng STEM",
    en: "Stage 01 // Foundation"
  },
  age: {
    vi: "4–6 tuổi",
    en: "Ages 4–6"
  },
  summary: {
    vi: "Học viên nhỏ tuổi hình thành trực giác không gian, tư duy nguyên nhân – kết quả và cảm quan logic đầu đời thông qua bộ giáo cụ cơ khí xúc giác LEGO chuyên dụng.",
    en: "Young learners cultivate spatial intuition, cause-and-effect reasoning, and early sensory logic with tactile mechanical kits."
  },
  milestone: {
    vi: "Lắp ráp mô hình động học tương tác và làm chủ chuỗi thao tác trực quan.",
    en: "Builds responsive kinetic structures and masters sequencing."
  },
  skills: {
    vi: ["Cơ khí xúc giác", "Tư duy chuỗi trực quan", "Câu đố không gian"],
    en: ["Sensory Mechanics", "Visual Sequencing", "Spatial Puzzles"]
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/early_tactile_mechanical_exploration.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/early_tactile_mechanical_exploration.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/early_tactile_mechanical_exploration.png-wvc-sizes",
  iconName: "compass"
}, {
  step: "02",
  title: {
    vi: "Kiến tạo (Build)",
    en: "Build"
  },
  tag: {
    vi: "Giai đoạn 02 // Kết cấu & Động cơ",
    en: "Stage 02 // Construction"
  },
  age: {
    vi: "7–9 tuổi",
    en: "Ages 7–9"
  },
  summary: {
    vi: "Trẻ làm quen với hệ bánh răng truyền động, khung gầm chịu lực và liên kết motor, thấu hiểu nguyên lý đòn bẩy cùng lợi thế cơ học thực tiễn.",
    en: "Children assemble structural gear trains, chassis frames, and motor-driven linkages while learning mechanical advantage."
  },
  milestone: {
    vi: "Chế tạo phương tiện đa bánh răng và làm chủ nguyên lý tỷ số truyền động.",
    en: "Constructs multi-gear vehicles and understands gear ratios."
  },
  skills: {
    vi: ["Hệ bánh răng & Tỷ số truyền", "Liên kết truyền động", "Lắp ráp khung gầm"],
    en: ["Gear Trains & Ratios", "Motorized Linkages", "Chassis Assembly"]
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/gear_motor_chassis_construction.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/gear_motor_chassis_construction.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/gear_motor_chassis_construction.png-wvc-sizes",
  iconName: "hammer"
}, {
  step: "03",
  title: {
    vi: "Lập trình (Code)",
    en: "Code"
  },
  tag: {
    vi: "Giai đoạn 03 // Tư duy Coding & AI",
    en: "Stage 03 // Logic"
  },
  age: {
    vi: "10–12 tuổi",
    en: "Ages 10–12"
  },
  summary: {
    vi: "Học viên chuyển tiếp sang tư duy điện toán, vòng lặp phản hồi cảm biến và phát triển thuật toán văn bản cho Robotics và các mô hình tự hành.",
    en: "Students transition into computational thinking, sensory feedback loops, and text-based algorithm development for their robots."
  },
  milestone: {
    vi: "Lập trình Coding cho robot tự hành né chướng ngại vật và bám vạch cảm biến.",
    en: "Programs autonomous obstacle avoidance and line followers."
  },
  skills: {
    vi: ["Coding Python từ khối lệnh", "Tích hợp cảm biến", "Mô hình trạng thái"],
    en: ["Block-to-Text Python", "Sensor Integration", "State Machines"]
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/student_coding_robotics_screen.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/student_coding_robotics_screen.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/student_coding_robotics_screen.png-wvc-sizes",
  iconName: "terminal"
}, {
  step: "04",
  title: {
    vi: "Kỹ sư chuyên sâu (Engineer)",
    en: "Engineer"
  },
  tag: {
    vi: "Giai đoạn 04 // Đấu trường & Engineering",
    en: "Stage 04 // Competition"
  },
  age: {
    vi: "13–16 tuổi",
    en: "Ages 13–16"
  },
  summary: {
    vi: "Học viên tự thiết kế hệ thống Robotics độc bản, đấu nối vi điều khiển, phân tích dữ liệu cảm biến và tranh tài tại các giải đấu công nghệ quốc tế.",
    en: "Students design custom robotic systems, wire microcontrollers, analyze telemetry, and compete in international arena circuits."
  },
  milestone: {
    vi: "Ứng dụng bản vẽ CAD độc lập và tham gia các giải đấu quốc gia & khu vực.",
    en: "Deploys custom CAD designs and competes in national tournaments."
  },
  skills: {
    vi: ["Thiết kế mẫu CAD", "Phân tích dữ liệu từ xa", "Chiến thuật thi đấu"],
    en: ["CAD Prototyping", "Telemetry Analysis", "Tournament Strategy"]
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/autonomous_robotics_engineering_lab.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/autonomous_robotics_engineering_lab.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/autonomous_robotics_engineering_lab.png-wvc-sizes",
  iconName: "cpu"
}];
const stageKeywords: Record<string, string[]> = {
  "01": ["lego robotics", "lego"],
  "02": ["lego robotics", "engineering"],
  "03": ["coding"],
  "04": ["robotics", "engineering"]
};
function getMatchingProgram(step: string, posts: Post[]): Post | undefined {
  const keywords = stageKeywords[step] || [];
  return posts.find(p => {
    const rawName = (p as any)?.acf?.program_name || p?.customFields?.program_name || p?.title || "";
    const lowerName = String(rawName).toLowerCase().trim();
    if (!lowerName) return false;
    return keywords.some(kw => lowerName.includes(kw));
  });
}
function JourneyConsumer({
  lang
}: {
  lang: "vi" | "en";
}) {
  const shouldReduceMotion = useReducedMotion();
  const {
    posts
  } = usePosts();
  const getStageIcon = (type: Stage["iconName"]) => {
    switch (type) {
      case "compass":
        return <Compass className="size-5 text-secondary" />;
      case "hammer":
        return <Hammer className="size-5 text-secondary" />;
      case "terminal":
        return <Terminal className="size-5 text-secondary" />;
      case "cpu":
        return <Cpu className="size-5 text-secondary" />;
    }
  };
  return <div className="py-10 lg:py-14">
      {/* Prominent High-Level Visual Timeline Bar */}
      <div className="relative mb-12 lg:mb-16">
        {/* Spec rail coordinate marks */}
        <div className="hidden lg:flex items-center justify-between text-[10px] font-mono text-muted-foreground uppercase tracking-widest px-8 pb-3">
          <span>// STAGE 01 : FOUNDATION</span>
          <span>// STAGE 02 : CONSTRUCTION</span>
          <span>// STAGE 03 : COMPUTATION</span>
          <span>// STAGE 04 : ADVANCED R&amp;D</span>
        </div>

        {/* Sweeping Timeline Connector on Large Screens */}
        <div className="hidden lg:block relative py-6 px-4 bg-card/60 backdrop-blur-xs border border-border rounded-xl shadow-xs">
          {/* Background Track with Notch Pattern */}
          <div className="absolute top-1/2 left-10 right-10 h-1.5 -translate-y-1/2 bg-muted rounded-lg overflow-hidden">
            {/* Glowing animated gradient timeline line */}
            <motion.div className="h-full w-full bg-[linear-gradient(90deg,var(--color-primary)_0%,var(--color-secondary)_35%,var(--color-accent)_70%,var(--color-chart-4)_100%)] opacity-90" initial={{
            scaleX: 0.95,
            opacity: 0.8
          }} animate={shouldReduceMotion ? undefined : {
            opacity: [0.8, 1, 0.8],
            filter: ["drop-shadow(0 0 4px var(--color-secondary))", "drop-shadow(0 0 8px var(--color-accent))", "drop-shadow(0 0 4px var(--color-secondary))"]
          }} transition={shouldReduceMotion ? undefined : {
            duration: 3.5,
            repeat: Infinity,
            ease: "easeInOut"
          }} />
          </div>

          <div className="grid grid-cols-4 gap-6 relative z-10">
            {stages.map((stage, idx) => <div key={stage.step} data-index={idx} className="flex items-center min-w-0 gap-3.5 px-3 py-2 bg-background/90 rounded-lg border border-border/80 shadow-xs">
                <div className="relative shrink-0 flex items-center justify-center">
                  <motion.span className="absolute -inset-1.5 rounded-full bg-secondary/30 pointer-events-none" animate={shouldReduceMotion ? undefined : {
                scale: [1, 1.3, 1],
                opacity: [0.35, 0.85, 0.35]
              }} transition={shouldReduceMotion ? undefined : {
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: idx * 0.4
              }} />
                  <div className="relative size-11 rounded-lg border-2 border-secondary bg-card text-secondary font-mono font-bold text-sm flex items-center justify-center shadow-sm">
                    {stage.step}
                  </div>
                </div>

                <div className="min-w-0">
                  <span className="block font-mono text-[11px] text-secondary font-semibold uppercase tracking-wider">
                    {stage.age[lang]}
                  </span>
                  <span className="font-serif font-bold text-foreground text-sm truncate block">
                    {stage.title[lang]}
                  </span>
                </div>
              </div>)}
          </div>
        </div>

        {/* Mobile / Tablet Compact Timeline Indicator */}
        <div className="lg:hidden flex items-center justify-between gap-2 p-3 bg-card border border-border rounded-lg mb-4 overflow-x-auto">
          {stages.map((stage, idx) => <div key={stage.step} data-index={idx} className="flex items-center gap-2 shrink-0 pr-3 border-r border-border last:border-r-0">
              <span className="size-6 rounded-md bg-secondary/15 text-secondary border border-secondary/40 font-mono text-xs font-bold flex items-center justify-center">
                {stage.step}
              </span>
              <span className="font-serif text-xs font-semibold text-foreground">
                {stage.age[lang]}
              </span>
            </div>)}
        </div>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stages.map((stage, idx) => {
        const matchedProgram = getMatchingProgram(stage.step, posts);
        return <div key={stage.step} data-index={idx} className="h-full">
              <Card className="h-full border border-border bg-card rounded-xl overflow-hidden hover:border-secondary transition-[border-color,box-shadow] duration-200 hover:shadow-md flex flex-col justify-between">
                <div>
                  {/* Visual Media Element */}
                  <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
                    <img src={stage.image} data-wvc-srcset={stage.srcset} data-wvc-sizes={stage.sizes} alt={`${stage.title[lang]} stage at ROBOTO Academy`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    <div className="absolute top-3 right-3 bg-primary/90 text-primary-foreground font-mono text-xs px-2.5 py-1 rounded-md backdrop-blur-xs">
                      {stage.age[lang]}
                    </div>
                    {/* Stage icon container */}
                    <div className="absolute bottom-3 left-3 bg-card/95 text-card-foreground p-2 rounded-lg shadow-xs border border-secondary/30 ring-2 ring-secondary/15 backdrop-blur-xs">
                      {getStageIcon(stage.iconName)}
                    </div>
                  </div>

                  <CardContent className="p-6">
                    <div className="font-mono text-xs text-secondary font-semibold tracking-wider mb-2">
                      {stage.tag[lang]}
                    </div>
                    <h3 className="font-serif text-xl font-semibold text-card-foreground mb-3">
                      {stage.title[lang]}
                    </h3>
                    <p className="font-sans text-sm text-muted-foreground leading-relaxed mb-4">
                      {stage.summary[lang]}
                    </p>

                    {/* Milestone Proof Point */}
                    <div className="bg-muted/70 rounded-lg p-3 border border-border/80 mb-5">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="size-4 text-secondary shrink-0 mt-0.5" />
                        <p className="font-sans text-xs text-foreground font-medium leading-snug">
                          <span className="font-semibold text-foreground">
                            {content.goalLabel[lang]}
                          </span>{" "}
                          {stage.milestone[lang]}
                        </p>
                      </div>
                    </div>

                    {/* Core Skills Chips */}
                    <div>
                      <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-wider mb-2">
                        {content.competenciesLabel[lang]}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {stage.skills[lang].map((skill, sIdx) => <span key={skill} data-index={sIdx} className="font-mono text-[11px] bg-background border border-border px-2 py-0.5 rounded-lg text-foreground/85">
                            {skill}
                          </span>)}
                      </div>
                    </div>
                  </CardContent>
                </div>

                <div className="px-6 pb-6 pt-0 border-t border-border mt-4 flex flex-col gap-2.5">
                  {/* Dynamic Matching Program Link */}
                  {matchedProgram?.link && <div className="pt-3">
                      <Link to={matchedProgram.link} className="inline-flex items-center gap-1.5 text-xs font-semibold text-secondary hover:text-secondary/80 transition-colors">
                        <BookOpen className="size-3.5 shrink-0" />
                        <span>{content.relatedProgram[lang]}</span>
                        <ArrowRight className="size-3 shrink-0" />
                      </Link>
                    </div>}

                  <Link to="/ProgramsPage" className="inline-flex items-center gap-1.5 pt-2 text-xs font-semibold font-serif text-foreground hover:text-secondary transition-colors">
                    <span>{content.curriculumDetails[lang]}</span>
                    <ArrowRight className="size-3.5" />
                  </Link>
                </div>
              </Card>
            </div>;
      })}
      </div>
    </div>;
}
export default function Journey() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    try {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "en" || stored === "vi") {
        setLang(stored);
      }
    } catch {
      // Safe fallback if localStorage is unavailable
    }
    const handleCustomChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang?: "vi" | "en";
      }>;
      const nextLang = customEvent.detail?.lang;
      if (nextLang === "vi" || nextLang === "en") {
        setLang(nextLang);
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const wp_query_params = {
    post_type: "program",
    per_page: 20,
    status: ["publish"]
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="the-learning-journey" data-nav="light" className="bg-background text-foreground relative w-full py-20 lg:py-24 border-b border-border overflow-hidden">
      {/* Decorative Mascot Floating Visual */}
      <div className="hidden xl:block pointer-events-none select-none absolute top-8 left-8 2xl:left-16 z-0">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        rotate: -2
      }} whileInView={{
        opacity: 1,
        scale: 1,
        rotate: -2
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="w-32 2xl:w-36 flex items-center justify-center pointer-events-none select-none">
          <img src={defaultMascot.src} data-wvc-srcset={defaultMascot.srcset} data-wvc-sizes={defaultMascot.sizes} alt={defaultMascot.alt} loading="lazy" decoding="async" className="w-full h-auto object-contain bg-transparent pointer-events-none select-none drop-shadow-md" />
        </motion.div>
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1440px] px-5 sm:px-8 md:px-12 lg:px-16 xl:px-[240px]">
        {/* Spec Rail Motif */}
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1.5 h-1.5 bg-secondary rounded-xs" />
          <div className="h-[1px] w-12 bg-secondary" />
          <span className="font-mono text-xs uppercase tracking-widest text-secondary font-semibold">
            {content.specRail[lang]}
          </span>
        </div>

        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 pb-12 border-b border-border">
          <div className="max-w-2xl">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">
              {content.heading[lang]}
            </h2>
            <p className="mt-4 font-sans text-base sm:text-lg text-muted-foreground leading-relaxed">
              {content.description[lang]}
            </p>
          </div>

          <div className="shrink-0 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground font-serif font-semibold">
              <Link to="/FreeTrialContactPage">
                {content.bookTrial[lang]}
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-border hover:bg-muted text-foreground">
              <Link to="/ProgramsPage">{content.viewCourses[lang]}</Link>
            </Button>
          </div>
        </div>

        {/* Wrapped with PostsProvider for Dynamic Program Matching */}
        <PostsProvider wp_query={{
        post_type: "program",
        per_page: 20,
        status: ["publish"]
      }}>
          <JourneyConsumer lang={lang} />
        </PostsProvider>

        {/* Bottom Educational Assurance Note */}
        <div className="mt-8 rounded-xl bg-muted/50 border border-border p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-card border border-border flex items-center justify-center shrink-0 text-secondary">
              <Sparkles className="size-5" />
            </div>
            <div>
              <h4 className="font-serif text-base sm:text-lg font-semibold text-foreground">
                {content.noteTitle[lang]}
              </h4>
              <p className="font-sans text-sm text-muted-foreground mt-1 max-w-2xl leading-relaxed">
                {content.noteDescription[lang]}
              </p>
            </div>
          </div>
          <Button asChild variant="outline" className="border-border bg-card hover:bg-muted text-foreground font-serif shrink-0">
            <Link to="/ParentGuideResourcesPage">{content.readGuide[lang]}</Link>
          </Button>
        </div>
      </div>
    </section>;
}